import { useForm, type UseFormReturn } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@mint-lib/i18n';
import { z } from 'zod';

/**
 * Ported from the AppsFlyer slice of the legacy
 * `useTraffickingAddFormMethods`/`useTraffickingManageFormMethods` hooks
 * (`packages/connections/src/features/AddConnectionOAuth/useTraffickingAddFormMethods.ts`
 * and `.../TraffickingManageConnectionForm/useTraffickingManageFormMethods.ts`).
 */

function createConnectionNameSchema(
  t: (key: string) => string,
  connectionName?: string | null,
  existingConnectionNames: readonly string[] = [],
) {
  const normalizedConnectionName = (connectionName ?? '').trim().toLowerCase();
  const normalizedExistingConnectionNames = new Set(
    existingConnectionNames.map((name) => name.trim().toLowerCase()),
  );

  return z
    .string()
    .trim()
    .min(1, { message: t('This field is required.') })
    .refine((name) => {
      const normalizedName = name.trim().toLowerCase();

      if (normalizedName === normalizedConnectionName) {
        return true;
      }

      return !normalizedExistingConnectionNames.has(normalizedName);
    }, t('The Connection Name field must be unique. Please specify another name.'));
}

export interface AddConnectionFormValues {
  name: string;
  apiToken: string;
  androidId: string;
  iosId: string;
}

export interface UseAppsFlyerAddFormMethodsParams {
  connectionName?: string | null;
  existingConnectionNames?: readonly string[];
}

/** RHF methods for the AppsFlyer add-connection form: name + token + at least one app id. */
export function useAppsFlyerAddFormMethods({
  connectionName,
  existingConnectionNames = [],
}: UseAppsFlyerAddFormMethodsParams): UseFormReturn<AddConnectionFormValues> {
  const { t } = useTranslation('@mint-lib/appsflyer-integration');
  const connectionNameSchema = createConnectionNameSchema(
    t,
    connectionName,
    existingConnectionNames,
  );

  return useForm<AddConnectionFormValues>({
    mode: 'all',
    resolver: zodResolver(
      z
        .object({
          name: connectionNameSchema,
          apiToken: z
            .string()
            .min(1, { message: t('This field is required.') }),
          androidId: z.string(),
          iosId: z.string(),
        })
        .superRefine((data, ctx) => {
          if (!data.androidId && !data.iosId) {
            ctx.addIssue({
              code: 'custom',
              path: ['androidId', 'iosId'],
              message: t('Required'),
            });
          }
        }),
    ),
    defaultValues: {
      name: connectionName || '',
      apiToken: '',
      androidId: '',
      iosId: '',
    },
  });
}

export interface ManageConnectionFormValues {
  name: string;
  apiToken: string | null;
  androidId: string | null;
  iosId: string | null;
}

export interface UseAppsFlyerManageFormMethodsParams {
  connectionName?: string | null;
  existingConnectionNames?: readonly string[];
}

/** RHF methods for the AppsFlyer manage-connection form: rename + reconnect-with-token. */
export function useAppsFlyerManageFormMethods({
  connectionName,
  existingConnectionNames = [],
}: UseAppsFlyerManageFormMethodsParams): UseFormReturn<ManageConnectionFormValues> {
  const { t } = useTranslation('@mint-lib/appsflyer-integration');
  const connectionNameSchema = createConnectionNameSchema(
    t,
    connectionName,
    existingConnectionNames,
  );

  return useForm<ManageConnectionFormValues>({
    mode: 'all',
    resolver: zodResolver(
      z
        .object({
          name: connectionNameSchema,
          apiToken: z.string().nullable(),
          androidId: z.string().nullable(),
          iosId: z.string().nullable(),
        })
        .superRefine((data, ctx) => {
          if (!data.androidId && !data.iosId) {
            ctx.addIssue({
              code: 'custom',
              path: ['androidId'],
              message: t('Required'),
            });
            ctx.addIssue({
              code: 'custom',
              path: ['iosId'],
              message: t('Required'),
            });
          }
        }),
    ),
    defaultValues: {
      name: '',
      apiToken: null,
      androidId: null,
      iosId: null,
    },
  });
}
